import React, {useMemo, useState, useEffect} from "react";
import {Timeline, Divider, Button, Modal, Select, Tag} from "antd";
import moment from "moment";
import {
    InfoCircleOutlined,
    PlusCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    MailOutlined,
    SwapOutlined,
    FileTextOutlined,
    UserOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import {BookingHistoryByCourseIdQuery, BookingHistoryAction} from "../../generated/graphql";
import css from "./BookingHistory.less";
import {renderUpdateDate, renderUser, formatHistoryDate} from "./bookingHistoryRenderHelpers";
import BookingHistoryDetails from "./BookingHistoryDetails";
import {parseDescription} from "./bookingHistoryDescription";
import TagComponent from "../../views/Tag/Tag";

const {Option} = Select;

type HistoryItem = BookingHistoryByCourseIdQuery["bookingHistoryByCourseId"][number];

type HistoryItemProps = {
    item: HistoryItem;
};

// Icon und Farbe für Aktionstypen
type ActionConfig = {
    icon: React.ReactNode;
    color: string;
};

const getActionConfig = (action: BookingHistoryAction): ActionConfig => {
    switch (action) {
        // Erstellen
        case BookingHistoryAction.BookingCreate:
        case BookingHistoryAction.WaitlistCreate:
        case BookingHistoryAction.PlaceReservationCreate:
        case BookingHistoryAction.PlaceReservationCreateByMaxAttendeesIncrease:
        case BookingHistoryAction.Created:
            return {icon: <PlusCircleOutlined />, color: "green"};

        // Löschen
        case BookingHistoryAction.BookingDelete:
        case BookingHistoryAction.BookingDeleteTransfer:
        case BookingHistoryAction.BookingOrphanDelete:
        case BookingHistoryAction.WaitlistDelete:
        case BookingHistoryAction.WaitlistDeleteTransfer:
        case BookingHistoryAction.PlaceReservationDelete:
            return {icon: <DeleteOutlined />, color: "red"};

        // Transfer/Verschieben
        case BookingHistoryAction.BookingCreateTransfer:
        case BookingHistoryAction.BookingCreateTransferMove:
        case BookingHistoryAction.WaitlistCreateTransfer:
        case BookingHistoryAction.WaitlistCreateTransferMove:
            return {icon: <SwapOutlined />, color: "orange"};

        // Kopieren
        case BookingHistoryAction.BookingCreateTransferCopy:
        case BookingHistoryAction.WaitlistCreateTransferCopy:
            return {icon: <FileTextOutlined />, color: "blue"};

        // Umwandlung
        case BookingHistoryAction.BookingToWaitlist:
        case BookingHistoryAction.WaitlistToBooking:
        case BookingHistoryAction.WaitlistToBookingByMaxAttendeesIncrease:
        case BookingHistoryAction.PlaceReservationToBooking:
            return {icon: <SwapOutlined />, color: "purple"};

        // Abrechnung/Bezahlt
        case BookingHistoryAction.BookingSettle:
        case BookingHistoryAction.ScheduledPaymentPaid:
            return {icon: <CheckCircleOutlined />, color: "green"};

        // Storniert
        case BookingHistoryAction.BookingCancle:
        case BookingHistoryAction.Canceled:
        case BookingHistoryAction.ScheduledPaymentUnpaid:
            return {icon: <CloseCircleOutlined />, color: "red"};

        // Preis/Zahlung
        case BookingHistoryAction.PriceChanged:
        case BookingHistoryAction.PaymentChanged:
        case BookingHistoryAction.ScheduledPaymentCreated:
        case BookingHistoryAction.ScheduledPaymentUpdated:
        case BookingHistoryAction.ScheduledPaymentDeleted:
        case BookingHistoryAction.PaymentMethodChanged:
            return {icon: <DollarOutlined />, color: "gold"};

        // Rechnung
        case BookingHistoryAction.CorrectionInvoice:
        case BookingHistoryAction.SendInvoice:
            return {icon: <FileTextOutlined />, color: "cyan"};

        // Email
        case BookingHistoryAction.SendBookingConfirmation:
        case BookingHistoryAction.SendWaitlistConfirmation:
        case BookingHistoryAction.EmailSent:
            return {icon: <MailOutlined />, color: "blue"};

        // Anwesenheit/Lektionen
        case BookingHistoryAction.AttendanceChanged:
            return {icon: <UserOutlined />, color: "geekblue"};
        case BookingHistoryAction.LessonAdded:
            return {icon: <PlusCircleOutlined />, color: "green"};
        case BookingHistoryAction.LessonRemoved:
            return {icon: <DeleteOutlined />, color: "red"};

        // Status/Update
        case BookingHistoryAction.StatusChanged:
        case BookingHistoryAction.Updated:
            return {icon: <EditOutlined />, color: "orange"};

        // Unbekannt/Custom
        case BookingHistoryAction.Custom:
        case BookingHistoryAction.Unknown:
        default:
            return {icon: <InfoCircleOutlined />, color: "default"};
    }
};

// Hilfsfunktion für Buchungsnummer (außerhalb der Komponente, damit sie wiederverwendbar ist)
const getBookingNumberFromHistoryItem = (item: HistoryItem): string => {
    // Versuche zuerst die direkten Felder aus der Historie
    if (item.bookingNumberPrefix && item.bookingNumber) {
        return `${item.bookingNumberPrefix}-${item.bookingNumber}`;
    }

    // Fallback: Verwende die booking-Relation falls vorhanden
    if (item.booking?.bookingNumberPrefix && item.booking?.bookingNumber) {
        return `${item.booking.bookingNumberPrefix}-${item.booking.bookingNumber}`;
    }

    // Fallback: Nur Prefix falls vorhanden
    if (item.bookingNumberPrefix) {
        return item.bookingNumberPrefix;
    }

    if (item.booking?.bookingNumberPrefix) {
        return item.booking.bookingNumberPrefix;
    }

    return "N/A";
};

const HistoryItem: React.FC<HistoryItemProps> = ({item}) => {
    const [open, setOpen] = useState(false);

    // Eintraege ohne Benutzer (Mollie-Webhook, Batch) tragen den Urheber nur im
    // gespeicherten userName ("System (Mollie-Webhook)") — vorher stand dort "N/A".
    const getUserInfo = (item: HistoryItem) => {
        const createdBy = item.createdBy;
        const userInfo = createdBy?.instructorDetails ? `${createdBy.instructorDetails.firstname} ${createdBy.instructorDetails.lastname}` : createdBy?.email ?? item.userName ?? "N/A";

        return (
            <TagComponent color="cyan" className={css.subSubLineItem}>
                {userInfo}
            </TagComponent>
        );
    };

    const getAttendeeName = (item: HistoryItem): string => {
        if (item.attendee?.firstname && item.attendee?.lastname) {
            return `${item.attendee.firstname} ${item.attendee.lastname}`;
        }

        return "Unbekannt";
    };

    const DetailsModal = () => {
        if (!open) {
            return (
                <Button type="link" size="small" onClick={() => setOpen(!open)} style={{padding: 0}} className={css.subSubLineItem}>
                    <InfoCircleOutlined />
                </Button>
            );
        }

        // Bug 2 (analog zu BookingHistoryContent.tsx): formatHistoryDate liefert
        // einen reinen String und vermeidet das Komma vor dem Datum, das durch
        // den React-Children-Array von renderUpdateDate entstand.
        const updateDate = formatHistoryDate(item.createDate);
        // Suffix " (durch Kursanpassung)" aus dem Modal-Titel entfernen -
        // wir zeigen ihn an anderer Stelle als Tag bzw. als Alert.
        const {cleanDescription: cleanTitleDescription} = parseDescription(item.description);
        const modalTitle = updateDate
            ? `${cleanTitleDescription} - ${updateDate}`
            : cleanTitleDescription;

        return (
            <Modal open={open} onCancel={() => setOpen(false)} title={modalTitle} width={1000} footer={null}>
                <BookingHistoryDetails item={item} />
            </Modal>
        );
    };

    const actionConfig = getActionConfig(item.action);

    // Farbzuordnung für Timeline-Dot (Ant Design Farben)
    const getDotStyle = (color: string) => {
        const colorMap: Record<string, {bg: string; border: string; icon: string}> = {
            green: {bg: "#f6ffed", border: "#52c41a", icon: "#389e0d"},
            red: {bg: "#fff1f0", border: "#ff4d4f", icon: "#cf1322"},
            orange: {bg: "#fff7e6", border: "#fa8c16", icon: "#d46b08"},
            blue: {bg: "#e6f7ff", border: "#1890ff", icon: "#0958d9"},
            purple: {bg: "#f9f0ff", border: "#722ed1", icon: "#531dab"},
            gold: {bg: "#fffbe6", border: "#faad14", icon: "#d48806"},
            cyan: {bg: "#e6fffb", border: "#13c2c2", icon: "#08979c"},
            geekblue: {bg: "#f0f5ff", border: "#2f54eb", icon: "#1d39c4"},
            default: {bg: "#fafafa", border: "#d9d9d9", icon: "#595959"},
        };

        return colorMap[color] ?? colorMap.default;
    };

    const dotStyle = getDotStyle(actionConfig.color);

    return (
        <Timeline.Item
            key={item.id}
            className={css.historyItem}
            dot={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: dotStyle.bg,
                        border: `2px solid ${dotStyle.border}`,
                    }}
                >
                    <span style={{color: dotStyle.icon, fontSize: "14px", display: "flex", alignItems: "center"}}>{actionConfig.icon}</span>
                </div>
            }
        >
            <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap"}}>
                {(() => {
                    // Bug 4: Identisches Pattern wie in BookingHistoryContent.tsx -
                    // Suffix " (durch Kursanpassung)" aus dem Action-Tag heraus-
                    // ziehen und stattdessen als separater purple Tag rendern.
                    // Wiederverwendet `parseDescription` aus
                    // bookingHistoryDescription.ts, KEINE Code-Duplizierung.
                    const parsed = parseDescription(item.description);

                    return (
                        <>
                            <Tag icon={actionConfig.icon} color={actionConfig.color} style={{margin: 0, fontSize: "0.95em"}}>
                                {parsed.cleanDescription || item.description}
                            </Tag>
                            {parsed.isCourseEdit && (
                                <Tag color="purple" style={{margin: 0, fontSize: "0.85em"}}>
                                    Durch Kursanpassung
                                </Tag>
                            )}
                        </>
                    );
                })()}
            </div>
            <div className={css.entryMedium + " " + css.grey}>{renderUpdateDate(item.createDate, true)}</div>
            <div className={css.entrySmall}>
                <Tag color="blue">{getBookingNumberFromHistoryItem(item)}</Tag>
                <Tag color="green">{getAttendeeName(item)}</Tag>
            </div>
            <div className={css.entrySmall}>{renderUser(item.user)}</div>
            <div className={css.subSubLine}>
                <div className={css.userInfo}>{getUserInfo(item)}</div>
                <DetailsModal />
            </div>
        </Timeline.Item>
    );
};

type CourseHistoryContentProps = {
    history: HistoryItem[];
};

const CourseHistoryContent: React.FC<CourseHistoryContentProps> = ({history}) => {
    const [groupBy, setGroupBy] = useState<"booking" | "time">("booking");
    const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);

    // Deutsche Lokalisierung aktivieren (nur einmal beim Mount)
    useEffect(() => {
        moment.locale("de");
    }, []);

    // Eindeutige Buchungs-IDs extrahieren
    const uniqueBookingIds = useMemo(() => {
        const bookingIds = new Set<string>();

        history.forEach((item) => {
            if (item.bookingId) {
                bookingIds.add(item.bookingId);
            }
        });

        return Array.from(bookingIds);
    }, [history]);

    // Buchungsnummern für Filter
    const bookingOptions = useMemo(() => {
        return uniqueBookingIds
            .map((bookingId) => {
                const firstItem = history.find((item) => item.bookingId === bookingId);

                if (!firstItem) {
                    return null;
                }

                const bookingNumber = getBookingNumberFromHistoryItem(firstItem);
                const attendeeName = firstItem.attendee?.firstname && firstItem.attendee?.lastname ? `${firstItem.attendee.firstname} ${firstItem.attendee.lastname}` : "Unbekannt";

                return {
                    bookingId,
                    label: `${bookingNumber} - ${attendeeName}`,
                };
            })
            .filter(Boolean) as Array<{bookingId: string; label: string}>;
    }, [uniqueBookingIds, history]);

    // Gefilterte Historie
    const filteredHistory = useMemo(() => {
        if (selectedBookingId) {
            return history.filter((item) => item.bookingId === selectedBookingId);
        }

        return history;
    }, [history, selectedBookingId]);

    // Gruppierung nach Buchungen - IMMER berechnen (Hook-Regel!)
    const groupedByBooking = useMemo(() => {
        const grouped: Record<string, HistoryItem[]> = {};

        filteredHistory.forEach((item) => {
            const key = item.bookingId ?? "unbekannt";
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(item);
        });

        // Sortiere innerhalb jeder Gruppe nach Datum (neueste zuerst)
        Object.keys(grouped).forEach((key) => {
            grouped[key].sort((a, b) => moment(b.createDate).valueOf() - moment(a.createDate).valueOf());
        });

        return grouped;
    }, [filteredHistory]);

    // Gruppierung nach Zeit - IMMER berechnen (Hook-Regel!)
    const groupedByTime = useMemo(() => {
        const today = moment().startOf("day");
        const grouped = {
            days: {} as Record<string, HistoryItem[]>,
            weeks: {} as Record<string, HistoryItem[]>,
            months: {} as Record<string, HistoryItem[]>,
        };

        filteredHistory.forEach((item) => {
            const itemDate = moment(item.createDate);
            const diffDays = today.diff(itemDate, "days");

            if (diffDays < 7) {
                const dayKey = itemDate.format("DD.MM.YYYY");

                grouped.days[dayKey] = [...(grouped.days[dayKey] || []), item];
            } else if (diffDays < 30) {
                const weekKey = `Woche ${itemDate.format("WW, YYYY")}`;

                grouped.weeks[weekKey] = [...(grouped.weeks[weekKey] || []), item];
            } else {
                const monthKey = itemDate.format("MMMM YYYY");

                grouped.months[monthKey] = [...(grouped.months[monthKey] || []), item];
            }
        });

        return grouped;
    }, [filteredHistory]);

    if (history.length === 0) {
        return <div>{"Keine Historie gefunden"}</div>;
    }

    // Render-Logik basierend auf groupBy (keine Hooks mehr hier!)
    const renderContent = () => {
        if (groupBy === "booking") {
            return (
                <>
                    {Object.entries(groupedByBooking).map(([bookingId, items]) => {
                        const firstItem = items[0];
                        const bookingNumber = getBookingNumberFromHistoryItem(firstItem);
                        const attendeeName = firstItem.attendee?.firstname && firstItem.attendee?.lastname ? `${firstItem.attendee.firstname} ${firstItem.attendee.lastname}` : "Unbekannt";

                        return (
                            <div key={bookingId} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>
                                    {bookingNumber} - {attendeeName} ({items.length} {items.length === 1 ? "Eintrag" : "Einträge"})
                                </div>
                                <Timeline style={{paddingTop: 8}}>
                                    {items.map((item) => (
                                        <HistoryItem key={item.id} item={item} />
                                    ))}
                                </Timeline>
                            </div>
                        );
                    })}
                </>
            );
        }

        // Gruppierung nach Zeit
        return (
            <>
                {/* Einträge der letzten 7 Tage */}
                {Object.keys(groupedByTime.days).length > 0 && (
                    <>
                        <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                            {"Letzte 7 Tage"}
                        </Divider>
                        {Object.entries(groupedByTime.days).map(([day, items]) => (
                            <div key={day} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>{day}</div>
                                <Timeline style={{paddingTop: 8}}>
                                    {items.map((item) => (
                                        <HistoryItem key={item.id} item={item} />
                                    ))}
                                </Timeline>
                            </div>
                        ))}
                    </>
                )}

                {/* Einträge der letzten Wochen */}
                {Object.keys(groupedByTime.weeks).length > 0 && (
                    <>
                        <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                            {"Letzte Wochen"}
                        </Divider>
                        {Object.entries(groupedByTime.weeks).map(([week, items]) => (
                            <div key={week} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>{week}</div>
                                <Timeline style={{paddingTop: 8}}>
                                    {items.map((item) => (
                                        <HistoryItem key={item.id} item={item} />
                                    ))}
                                </Timeline>
                            </div>
                        ))}
                    </>
                )}

                {/* Einträge der letzten Monate */}
                {Object.keys(groupedByTime.months).length > 0 && (
                    <>
                        <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                            {"Ältere Einträge"}
                        </Divider>
                        {Object.entries(groupedByTime.months).map(([month, items]) => (
                            <div key={month} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>{month}</div>
                                <Timeline style={{paddingTop: 8}}>
                                    {items.map((item) => (
                                        <HistoryItem key={item.id} item={item} />
                                    ))}
                                </Timeline>
                            </div>
                        ))}
                    </>
                )}
            </>
        );
    };

    return (
        <div className={css.timelineContainer}>
            <div style={{marginBottom: 16, display: "flex", gap: 8, alignItems: "center"}}>
                <span style={{fontWeight: 500}}>Gruppierung:</span>
                <Select value={groupBy} onChange={(value) => setGroupBy(value)} style={{width: 150}}>
                    <Option value="booking">Nach Buchung</Option>
                    <Option value="time">Nach Zeit</Option>
                </Select>
                {bookingOptions.length > 0 && (
                    <>
                        <span style={{fontWeight: 500, marginLeft: 16}}>Filter:</span>
                        <Select value={selectedBookingId} onChange={(value) => setSelectedBookingId(value)} style={{width: 250}} allowClear placeholder="Alle Buchungen">
                            {bookingOptions.map((option) => (
                                <Option key={option.bookingId} value={option.bookingId}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </>
                )}
            </div>
            {renderContent()}
        </div>
    );
};

export default CourseHistoryContent;
